import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box, Button, TextField, CircularProgress, Autocomplete,
  FormControl, InputLabel, Select, MenuItem, FormHelperText,
  Chip, Tooltip, Typography, Divider,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { AddCircleOutline as CreateIcon, Warning as WarningIcon } from '@mui/icons-material';
import { debounce } from 'lodash';
import wineNameService, {
  ProducerOption, RegionOption, GrapeOption,
  WineColor, WineType, WineSubType, WineName,
} from '../../services/wineNameService';
import { parseVintageInput } from '../../services/wineService';
import { foldForMatch } from '../../utils/displayCase';

/**
 * THE wine-identity form — the pickers, field order and semantics of the
 * app's normal Add Wine flow (CreateWineModal), extracted so the operator
 * console renders the SAME flow pre-filled from a label scan instead of a
 * bespoke decision form.
 *
 * The single seam between hosts is `entityOps`: what the explicit
 * 'Create "<name>"' action in each dropdown actually does. The venue modal
 * keeps its existing on-the-fly creation (needsReview: true, unguarded —
 * unchanged behaviour); the console injects the guarded find-or-create,
 * where a fold-duplicate returns the existing entity preselected instead of
 * minting a twin. Search-first is the picker's own behaviour in both.
 */

// ── filter factories ──────────────────────────────────────────────────────────
const filterProducers = createFilterOptions<ProducerOption>();
const filterRegions = createFilterOptions<RegionOption>();
const filterGrapes = createFilterOptions<GrapeOption>();

// ── label maps ────────────────────────────────────────────────────────────────
// Insertion order is the canonical wine-color order — see WINE_COLOR_ORDER in
// utils/wineColor.
export const colorLabels: Record<WineColor, string> = {
  SPARKLING: 'Sparkling', WHITE: 'White', ORANGE: 'Orange', ROSE: 'Rosé',
  RED: 'Red', DESSERT: 'Dessert', FORTIFIED: 'Fortified',
};

export const typeLabels: Record<WineType, string> = {
  WINE: 'Wine', SPIRITS: 'Spirits', BEER: 'Beer', OTHER: 'Other',
};

export const subTypeLabels: Record<WineSubType, string> = {
  STILL: 'Still', SPARKLING: 'Sparkling', FORTIFIED: 'Fortified',
  DESSERT: 'Dessert', SWEET: 'Sweet', NATURAL: 'Natural',
};

// ── the entityOps seam ────────────────────────────────────────────────────────
/** Set on options the Create action minted during THIS form session. */
export interface MintTagged { __mintedNow?: boolean }

export interface EntityOps {
  createProducer: (name: string) => Promise<ProducerOption & { existed?: boolean }>;
  createRegion: (name: string) => Promise<RegionOption & { existed?: boolean }>;
  createGrape: (name: string) => Promise<GrapeOption & { existed?: boolean }>;
}

export interface WineIdentityPrefill {
  producerQuery?: string;
  regionQuery?: string;
  /** Grape names as read off the label (printed evidence only). */
  grapeQueries?: string[];
  specialName?: string;
  vintageInput?: string;
  /**
   * Console-prefill item 2: every read pre-fills its field. When set, each
   * query auto-SELECTS the entity whose name fold-matches the read; a read
   * that matches nothing becomes a PENDING chip (text + one-tap Create)
   * instead of being silently dropped. The venue modal leaves this off and
   * keeps its plain seeded-search behaviour.
   */
  autoResolve?: boolean;
}

export interface UseWineIdentityFormOptions {
  entityOps: EntityOps;
  /** Reset + prefill whenever this changes to a new truthy value. */
  prefillKey?: string | null;
  prefill?: WineIdentityPrefill;
}

export function useWineIdentityForm({ entityOps, prefillKey, prefill }: UseWineIdentityFormOptions) {
  // ── form state ────────────────────────────────────────────────────────────
  const [producer, setProducer] = useState<ProducerOption | null>(null);
  const [producerInputValue, setProducerInputValue] = useState('');
  const [region, setRegion] = useState<RegionOption | null>(null);
  const [regionInputValue, setRegionInputValue] = useState('');
  const [labelGrapes, setLabelGrapes] = useState<GrapeOption[]>([]);
  const [specialName, setSpecialName] = useState('');
  const [color, setColor] = useState<WineColor | ''>('');
  const [wineType, setWineType] = useState<WineType>('WINE');
  const [subType, setSubType] = useState<WineSubType | ''>('');
  const [vintageInput, setVintageInput] = useState('');

  // ── autocomplete options ──────────────────────────────────────────────────
  const [producerOptions, setProducerOptions] = useState<ProducerOption[]>([]);
  const [regionOptions, setRegionOptions] = useState<RegionOption[]>([]);
  const [grapeOptions, setGrapeOptions] = useState<GrapeOption[]>([]);

  // ── loading / status ──────────────────────────────────────────────────────
  const [loadingProducers, setLoadingProducers] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingGrapes, setLoadingGrapes] = useState(false);
  const [creatingOnFly, setCreatingOnFly] = useState(false);
  /** Set when a guarded create found a fold-duplicate and preselected it. */
  const [existingNotice, setExistingNotice] = useState('');

  // ── validation errors ─────────────────────────────────────────────────────
  const [errors, setErrors] = useState<{
    producer?: string; region?: string; color?: string; vintage?: string;
  }>({});
  const [opError, setOpError] = useState('');

  // ── duplicate state ───────────────────────────────────────────────────────
  // checkDuplicate matches the full identity tuple (producer + region +
  // specialName + exact grape set) — the same one the unique index enforces.
  const [wineNameDuplicate, setWineNameDuplicate] = useState<WineName | null>(null);

  // ── pending reads (item 2): read text that matched no entity ─────────────
  const [pendingProducer, setPendingProducer] = useState<string | null>(null);
  const [pendingRegion, setPendingRegion] = useState<string | null>(null);
  const [pendingGrapes, setPendingGrapes] = useState<string[]>([]);

  // ── reset + prefill ───────────────────────────────────────────────────────
  const prevKey = useRef<string | null>(null);
  useEffect(() => {
    if (!prefillKey || prefillKey === prevKey.current) return;
    prevKey.current = prefillKey;
    setProducer(null);
    setProducerInputValue(prefill?.producerQuery ?? '');
    setRegion(null);
    setRegionInputValue(prefill?.regionQuery ?? '');
    setLabelGrapes([]);
    setSpecialName(prefill?.specialName ?? '');
    setColor('');
    setWineType('WINE');
    setSubType('');
    setVintageInput(prefill?.vintageInput ?? '');
    setProducerOptions([]);
    setRegionOptions([]);
    setGrapeOptions([]);
    setErrors({});
    setOpError('');
    setExistingNotice('');
    setWineNameDuplicate(null);

    setPendingProducer(null);
    setPendingRegion(null);
    setPendingGrapes([]);

    // Kick off searches with the prefilled queries — the operator lands on a
    // form whose pickers already show what the store knows about the label.
    // With autoResolve (the console), a fold-exact hit SELECTS the entity;
    // a miss becomes a PENDING chip — nothing read is silently dropped.
    const auto = Boolean(prefill?.autoResolve);
    const pq = (prefill?.producerQuery ?? '').trim();
    if (pq.length >= 2) {
      setLoadingProducers(true);
      wineNameService.searchProducers(pq)
        .then((opts) => {
          setProducerOptions(opts);
          if (auto) {
            const hit = opts.find((o) =>
              foldForMatch(o.name) === foldForMatch(pq)
              || foldForMatch(o.fullName ?? '') === foldForMatch(pq));
            if (hit) setProducer(hit);
            else setPendingProducer(pq);
          }
        })
        .finally(() => setLoadingProducers(false));
    } else if (auto && pq) {
      setPendingProducer(pq);
    }
    const rq = (prefill?.regionQuery ?? '').trim();
    if (rq.length >= 2) {
      setLoadingRegions(true);
      wineNameService.searchRegions(rq)
        .then((opts) => {
          setRegionOptions(opts);
          if (auto) {
            const hit = opts.find((o) => foldForMatch(o.name) === foldForMatch(rq));
            if (hit) setRegion(hit);
            else setPendingRegion(rq);
          }
        })
        .finally(() => setLoadingRegions(false));
    } else if (auto && rq) {
      setPendingRegion(rq);
    }
    if (auto) {
      for (const gqRaw of prefill?.grapeQueries ?? []) {
        const gq = gqRaw.trim();
        if (!gq) continue;
        if (gq.length < 2) { setPendingGrapes((prev) => [...prev, gq]); continue; }
        wineNameService.searchGrapes(gq).then((opts) => {
          const hit = opts.find((o) => foldForMatch(o.primaryName) === foldForMatch(gq));
          if (hit) {
            setLabelGrapes((prev) => prev.some((g) => g._id === hit._id) || prev.length >= 3 ? prev : [...prev, hit]);
            setGrapeOptions((prev) => [hit, ...prev.filter((g) => g._id !== hit._id)]);
          } else {
            setPendingGrapes((prev) => prev.includes(gq) ? prev : [...prev, gq]);
          }
        }).catch(() => setPendingGrapes((prev) => prev.includes(gq) ? prev : [...prev, gq]));
      }
    }
  }, [prefillKey, prefill]);

  // A filled field retires its pending chip — the read has been answered.
  useEffect(() => { if (producer) setPendingProducer(null); }, [producer]);
  useEffect(() => { if (region) setPendingRegion(null); }, [region]);

  /** One-tap Create for a pending chip — guard-checked via entityOps. */
  const resolvePending = useCallback(async (kind: 'producer' | 'region' | 'grape', text: string) => {
    setCreatingOnFly(true);
    try {
      if (kind === 'producer') {
        const created = await entityOps.createProducer(text);
        (created as MintTagged).__mintedNow = !created.existed;
        setProducer(created);
        setProducerOptions((prev) => [created, ...prev.filter((o) => o._id !== created._id)]);
        if (created.existed) setExistingNotice(`“${created.fullName || created.name}” already exists — selected it instead of creating a duplicate.`);
      } else if (kind === 'region') {
        const created = await entityOps.createRegion(text);
        (created as MintTagged).__mintedNow = !created.existed;
        setRegion(created);
        setRegionOptions((prev) => [created, ...prev.filter((o) => o._id !== created._id)]);
        if (created.existed) setExistingNotice(`“${created.name}” already exists — selected it instead of creating a duplicate.`);
      } else {
        const created = await entityOps.createGrape(text);
        (created as MintTagged).__mintedNow = !created.existed;
        setLabelGrapes((prev) => prev.some((g) => g._id === created._id) || prev.length >= 3 ? prev : [...prev, created]);
        setGrapeOptions((prev) => [created, ...prev.filter((o) => o._id !== created._id)]);
        setPendingGrapes((prev) => prev.filter((t) => t !== text));
        if (created.existed) setExistingNotice(`“${created.primaryName}” already exists — selected it instead of creating a duplicate.`);
      }
    } catch (e: any) { if (!e?.cancelled) setOpError(`Failed to create ${kind}. Please try again.`); }
    finally { setCreatingOnFly(false); }
  }, [entityOps]);

  // ── debounced searches ────────────────────────────────────────────────────
  const searchProducers = useMemo(
    () => debounce(async (q: string) => {
      setLoadingProducers(true);
      try { setProducerOptions(await wineNameService.searchProducers(q)); }
      finally { setLoadingProducers(false); }
    }, 300),
    []
  );

  const searchRegions = useMemo(
    () => debounce(async (q: string) => {
      setLoadingRegions(true);
      try { setRegionOptions(await wineNameService.searchRegions(q)); }
      finally { setLoadingRegions(false); }
    }, 300),
    []
  );

  const searchGrapes = useMemo(
    () => debounce(async (q: string) => {
      setLoadingGrapes(true);
      try { setGrapeOptions(await wineNameService.searchGrapes(q)); }
      finally { setLoadingGrapes(false); }
    }, 300),
    []
  );

  // ── live duplicate check for the identity tuple ───────────────────────────
  const dupCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!producer) { setWineNameDuplicate(null); return; }
    if (dupCheckTimer.current) clearTimeout(dupCheckTimer.current);
    dupCheckTimer.current = setTimeout(async () => {
      try {
        const result = await wineNameService.checkDuplicate({
          producerId: producer._id,
          regionId: region?._id,
          specialName: specialName.trim() || undefined,
          labelGrapeIds: labelGrapes.map(g => g._id),
        });
        setWineNameDuplicate(result.isDuplicate && result.existing ? result.existing : null);
      } catch { /* ignore */ }
    }, 600);
  }, [producer, region, specialName, labelGrapes]);

  // ── create-on-pick handlers (the entityOps seam) ──────────────────────────
  const handleProducerChange = useCallback(async (_: any, newValue: ProducerOption | null) => {
    setExistingNotice('');
    if (!newValue) { setProducer(null); return; }
    if (newValue.__isNew__) {
      setCreatingOnFly(true);
      try {
        const created = await entityOps.createProducer(newValue.inputValue!);
        (created as MintTagged).__mintedNow = !created.existed;
        setProducer(created);
        setProducerOptions(prev => [created, ...prev.filter(p => p._id !== created._id)]);
        if (created.existed) setExistingNotice(`“${created.fullName || created.name}” already exists — selected it instead of creating a duplicate.`);
      } catch (e: any) { if (!e?.cancelled) setOpError('Failed to create producer. Please try again.'); }
      finally { setCreatingOnFly(false); }
    } else {
      setProducer(newValue);
    }
    setErrors(prev => ({ ...prev, producer: undefined }));
  }, [entityOps]);

  const handleRegionChange = useCallback(async (_: any, newValue: RegionOption | null) => {
    setExistingNotice('');
    if (!newValue) { setRegion(null); return; }
    if (newValue.__isNew__) {
      setCreatingOnFly(true);
      try {
        const created = await entityOps.createRegion(newValue.inputValue!);
        (created as MintTagged).__mintedNow = !created.existed;
        setRegion(created);
        setRegionOptions(prev => [created, ...prev.filter(r => r._id !== created._id)]);
        if (created.existed) setExistingNotice(`“${created.name}” already exists — selected it instead of creating a duplicate.`);
      } catch (e: any) { if (!e?.cancelled) setOpError('Failed to create region. Please try again.'); }
      finally { setCreatingOnFly(false); }
    } else {
      setRegion(newValue);
    }
    setErrors(prev => ({ ...prev, region: undefined }));
  }, [entityOps]);

  const handleGrapesChange = useCallback(async (_: any, newValues: GrapeOption[]) => {
    setExistingNotice('');
    if (newValues.length > 3) return;
    const last = newValues[newValues.length - 1];
    if (last?.__isNew__) {
      setCreatingOnFly(true);
      try {
        const created = await entityOps.createGrape(last.inputValue!);
        (created as MintTagged).__mintedNow = !created.existed;
        setLabelGrapes(prev => {
          const kept = newValues.slice(0, -1).filter(g => g._id !== created._id);
          return [...kept, created];
        });
        setGrapeOptions(prev => [created, ...prev.filter(g => g._id !== created._id)]);
        if (created.existed) setExistingNotice(`“${created.primaryName}” already exists — selected it instead of creating a duplicate.`);
      } catch (e: any) { if (!e?.cancelled) setOpError('Failed to create grape. Please try again.'); }
      finally { setCreatingOnFly(false); }
    } else {
      setLabelGrapes(newValues);
    }
  }, [entityOps]);

  // ── validation ────────────────────────────────────────────────────────────
  const validate = useCallback((opts?: { requireVintage?: boolean; requireRegion?: boolean }): boolean => {
    const newErrors: typeof errors = {};
    if (!producer) newErrors.producer = 'Producer is required';
    // P5d: the console never invents a region — where the API accepts null,
    // the form does too, and the label simply may not name one.
    if (!region && opts?.requireRegion !== false) newErrors.region = 'Region is required';
    if (!color) newErrors.color = 'Color is required';
    if (opts?.requireVintage !== false) {
      if (!vintageInput.trim()) {
        newErrors.vintage = 'Vintage is required';
      } else {
        const parsed = parseVintageInput(vintageInput);
        if (!parsed.valid) newErrors.vintage = parsed.error ?? 'Invalid vintage';
      }
    } else if (vintageInput.trim()) {
      const parsed = parseVintageInput(vintageInput);
      if (!parsed.valid) newErrors.vintage = parsed.error ?? 'Invalid vintage';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [producer, region, color, vintageInput]);

  return {
    // values
    producer, producerInputValue, region, regionInputValue, labelGrapes,
    specialName, color, wineType, subType, vintageInput,
    // setters the fields need
    setProducerInputValue, setRegionInputValue, setSpecialName, setColor,
    setWineType, setSubType, setVintageInput, setErrors,
    // options + loading
    producerOptions, regionOptions, grapeOptions,
    loadingProducers, loadingRegions, loadingGrapes, creatingOnFly,
    // searches + handlers
    searchProducers, searchRegions, searchGrapes,
    handleProducerChange, handleRegionChange, handleGrapesChange,
    // status
    errors, opError, setOpError, existingNotice, wineNameDuplicate,
    validate,
    // pending reads (item 2)
    pendingProducer, pendingRegion, pendingGrapes, setPendingGrapes, resolvePending,
    // for hosts that preselect (guard rescues, probe-backed creates) or
    // backfill the duplicate banner after a submit-time 409 raced the check
    setProducer, setProducerOptions, setRegion, setRegionOptions, setWineNameDuplicate,
  };
}

export type WineIdentityFormState = ReturnType<typeof useWineIdentityForm>;

/** The composed display name, exactly as WineNameForm previews it. */
export function buildDisplayNamePreview(
  producer: ProducerOption | null,
  region: RegionOption | null,
  grapes: GrapeOption[],
  specialName: string,
): string {
  const parts: string[] = [];
  if (producer) parts.push(producer.fullName || producer.name);
  grapes.forEach((g) => parts.push(g.primaryName));
  if (region) parts.push(region.name);
  if (specialName.trim()) parts.push(specialName.trim());
  const seen = new Set<string>();
  return parts.filter((p) => {
    const key = p.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).join(' ');
}

// ── the fields, exactly as the Add Wine flow renders them ─────────────────────

/** A read that matched nothing: its text, a one-tap Create, host extras. */
const PendingReadChip: React.FC<{
  kind: 'producer' | 'region' | 'grape';
  text: string;
  form: WineIdentityFormState;
  extra?: React.ReactNode;
}> = ({ kind, text, form, extra }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: -0.5, mb: 0.5 }}>
    <Chip size="small" color="warning" variant="outlined" label={`read: “${text}”`} />
    <Button
      size="small"
      sx={{ textTransform: 'none', py: 0, minWidth: 0 }}
      disabled={form.creatingOnFly}
      onClick={() => form.resolvePending(kind, text)}
    >
      Create “{text}”
    </Button>
    {extra}
  </Box>
);

export const WineIdentityFields: React.FC<{
  form: WineIdentityFormState;
  /** The venue modal requires vintage; the console allows a vintage-less approval. */
  vintageRequired?: boolean;
  /** P5d: the console allows region-less identities (the API does). */
  regionRequired?: boolean;
  /** Host-specific actions attached to a pending chip (e.g. Ask the web). */
  pendingExtras?: (kind: 'producer' | 'region' | 'grape', text: string) => React.ReactNode;
}> = ({ form, vintageRequired = true, regionRequired = true, pendingExtras }) => {
  const f = form;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.5 }}>
      {f.creatingOnFly && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">Creating…</Typography>
        </Box>
      )}

      {/* Producer */}
      <Autocomplete<ProducerOption>
        options={f.producerOptions}
        value={f.producer}
        inputValue={f.producerInputValue}
        loading={f.loadingProducers}
        isOptionEqualToValue={(o, v) => o._id === v._id}
        getOptionLabel={o => o.__isNew__ ? o.name : (o.fullName || o.name)}
        filterOptions={(options, params) => {
          const filtered = filterProducers(options, params);
          const { inputValue } = params;
          const exists = options.some(o => (o.fullName || o.name).toLowerCase() === inputValue.toLowerCase());
          if (inputValue !== '' && !exists) {
            filtered.push({ _id: '__new__', name: `Create "${inputValue}"`, __isNew__: true, inputValue });
          }
          return filtered;
        }}
        onInputChange={(_, val) => {
          f.setProducerInputValue(val);
          if (val.trim().length >= 2) f.searchProducers(val);
        }}
        onChange={f.handleProducerChange}
        renderOption={(props, option) => (
          <li {...props} key={option._id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {option.__isNew__ && <CreateIcon fontSize="small" color="primary" />}
              <Box>
                <Typography variant="body2" fontWeight={option.__isNew__ ? 600 : 400}>
                  {option.__isNew__ ? option.name : (option.fullName || option.name)}
                </Typography>
                {option.needsReview && !option.__isNew__ && (
                  <Typography variant="caption" color="warning.main">Needs review</Typography>
                )}
              </Box>
            </Box>
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label="Producer"
            required
            margin="normal"
            error={Boolean(f.errors.producer)}
            helperText={f.errors.producer || 'Search or type to create'}
            InputProps={{
              ...params.InputProps,
              endAdornment: <>{f.loadingProducers && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>,
            }}
          />
        )}
      />

      {!f.producer && f.pendingProducer && (
        <PendingReadChip kind="producer" text={f.pendingProducer} form={f}
          extra={pendingExtras?.('producer', f.pendingProducer)} />
      )}

      {/* Region */}
      <Autocomplete<RegionOption>
        options={f.regionOptions}
        value={f.region}
        inputValue={f.regionInputValue}
        loading={f.loadingRegions}
        isOptionEqualToValue={(o, v) => o._id === v._id}
        getOptionLabel={o => o.name}
        filterOptions={(options, params) => {
          const filtered = filterRegions(options, params);
          const { inputValue } = params;
          const exists = options.some(o => o.name.toLowerCase() === inputValue.toLowerCase());
          if (inputValue !== '' && !exists) {
            filtered.push({ _id: '__new__', name: `Create "${inputValue}"`, __isNew__: true, inputValue });
          }
          return filtered;
        }}
        onInputChange={(_, val) => {
          f.setRegionInputValue(val);
          if (val.trim().length >= 2) f.searchRegions(val);
        }}
        onChange={f.handleRegionChange}
        renderOption={(props, option) => (
          <li {...props} key={option._id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {option.__isNew__ && <CreateIcon fontSize="small" color="primary" />}
              <Box>
                <Typography variant="body2" fontWeight={option.__isNew__ ? 600 : 400}>{option.name}</Typography>
                {!option.__isNew__ && option.hierarchyNames && option.hierarchyNames.length > 1 && (
                  <Typography variant="caption" color="text.secondary">
                    {option.hierarchyNames.join(' › ')}
                  </Typography>
                )}
                {option.needsReview && !option.__isNew__ && (
                  <Typography variant="caption" color="warning.main">Needs review</Typography>
                )}
              </Box>
            </Box>
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label={regionRequired ? 'Region' : 'Region (optional)'}
            required={regionRequired}
            margin="normal"
            error={Boolean(f.errors.region)}
            helperText={f.errors.region || 'Search or type to create'}
            InputProps={{
              ...params.InputProps,
              endAdornment: <>{f.loadingRegions && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>,
            }}
          />
        )}
      />

      {!f.region && f.pendingRegion && (
        <PendingReadChip kind="region" text={f.pendingRegion} form={f}
          extra={pendingExtras?.('region', f.pendingRegion)} />
      )}

      {/* Label Grapes */}
      <Autocomplete<GrapeOption, true>
        multiple
        options={f.grapeOptions}
        value={f.labelGrapes}
        loading={f.loadingGrapes}
        isOptionEqualToValue={(o, v) => o._id === v._id}
        getOptionLabel={o => o.primaryName}
        filterOptions={(options, params) => {
          const filtered = filterGrapes(options, params);
          const { inputValue } = params;
          const exists = options.some(o => o.primaryName.toLowerCase() === inputValue.toLowerCase());
          if (inputValue !== '' && !exists && f.labelGrapes.length < 3) {
            filtered.push({ _id: '__new__', primaryName: `Create "${inputValue}"`, __isNew__: true, inputValue });
          }
          return filtered;
        }}
        onInputChange={(_, val) => { if (val.trim().length >= 2) f.searchGrapes(val); }}
        onChange={f.handleGrapesChange}
        getOptionDisabled={() => f.labelGrapes.length >= 3}
        renderOption={(props, option) => (
          <li {...props} key={option._id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {option.__isNew__ && <CreateIcon fontSize="small" color="primary" />}
              <Box>
                <Typography variant="body2" fontWeight={option.__isNew__ ? 600 : 400}>
                  {option.primaryName}
                </Typography>
                {!option.__isNew__ && option.color && (
                  <Typography variant="caption" color="text.secondary">
                    {option.color.replace('_', ' / ')}
                  </Typography>
                )}
                {option.needsReview && !option.__isNew__ && (
                  <Typography variant="caption" color="warning.main">Needs review</Typography>
                )}
              </Box>
            </Box>
          </li>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...other } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={option.primaryName}
                size="small"
                icon={option.needsReview
                  ? <Tooltip title="Needs review"><WarningIcon sx={{ fontSize: '14px !important' }} /></Tooltip>
                  : undefined}
                {...other}
              />
            );
          })
        }
        renderInput={params => (
          <TextField
            {...params}
            label="Label Grapes"
            margin="normal"
            helperText={f.labelGrapes.length >= 3 ? 'Maximum 3 grapes' : 'Optional — up to 3'}
            InputProps={{
              ...params.InputProps,
              endAdornment: <>{f.loadingGrapes && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>,
            }}
          />
        )}
      />

      {f.pendingGrapes.map((g) => (
        <PendingReadChip key={g} kind="grape" text={g} form={f}
          extra={pendingExtras?.('grape', g)} />
      ))}

      {/* Special Name */}
      <TextField
        fullWidth
        label="Special Name"
        value={f.specialName}
        onChange={e => f.setSpecialName(e.target.value)}
        margin="normal"
        helperText='Optional — cuvée or vineyard name'
      />

      <Divider sx={{ my: 1 }} />

      {/* Color + Type */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
        <FormControl required error={Boolean(f.errors.color)}>
          <InputLabel id="wine-identity-color-label">Color</InputLabel>
          <Select
            labelId="wine-identity-color-label"
            value={f.color}
            onChange={e => {
              f.setColor(e.target.value as WineColor);
              f.setErrors(prev => ({ ...prev, color: undefined }));
            }}
            label="Color"
          >
            {(Object.entries(colorLabels) as [WineColor, string][]).map(([val, label]) => (
              <MenuItem key={val} value={val}>{label}</MenuItem>
            ))}
          </Select>
          {f.errors.color && <FormHelperText>{f.errors.color}</FormHelperText>}
        </FormControl>

        <FormControl>
          <InputLabel>Type</InputLabel>
          <Select
            value={f.wineType}
            onChange={e => f.setWineType(e.target.value as WineType)}
            label="Type"
          >
            {(Object.entries(typeLabels) as [WineType, string][]).map(([val, label]) => (
              <MenuItem key={val} value={val}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Sub Type */}
      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel>Sub Type</InputLabel>
        <Select
          value={f.subType}
          onChange={e => f.setSubType(e.target.value as WineSubType | '')}
          label="Sub Type"
        >
          <MenuItem value="">— None —</MenuItem>
          {(Object.entries(subTypeLabels) as [WineSubType, string][]).map(([val, label]) => (
            <MenuItem key={val} value={val}>{label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider sx={{ my: 2 }} />

      {/* Vintage */}
      <TextField
        fullWidth
        label="Vintage"
        required={vintageRequired}
        value={f.vintageInput}
        onChange={e => {
          f.setVintageInput(e.target.value);
          f.setErrors(prev => ({ ...prev, vintage: undefined }));
        }}
        error={Boolean(f.errors.vintage)}
        helperText={f.errors.vintage || 'e.g. 2018, NV, MV'}
        placeholder="2018"
      />
    </Box>
  );
};
